import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css';
import 'filepond/dist/filepond.min.css';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Typography from '@mui/material/Typography';
import { EImageVariant } from '@onesource/schemas';
import { FileStatus, type ProcessServerConfigFunction } from 'filepond';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import FilePondPluginImageExifOrientation from 'filepond-plugin-image-exif-orientation';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import type { FilePond as FilePondInstance } from 'react-filepond';
import { FilePond, registerPlugin } from 'react-filepond';

import { CropDialog } from './crop-dialog';
import { ALL_VARIANTS_NO_ORIGINAL, EMimeType, FILE_FORMATS, VARIANT_OPTIONS } from './file-uploader.constants';
import { RenameFileDialog } from './rename-file-dialog';
import { useCropQueue } from './use-crop-queue';

export { EMimeType, FILE_FORMATS };

registerPlugin(
  FilePondPluginFileValidateType,
  FilePondPluginFileValidateSize,
  FilePondPluginImagePreview,
  FilePondPluginImageExifOrientation,
);

export type TFileUploaderProps = {
  /**
   * Explicit list of accepted MIME types.
   * Use FILE_FORMATS presets or build a custom list.
   * @example ['image/jpeg', 'image/png']
   * @example FILE_FORMATS.IMAGE_AND_PDF
   */
  allowedFormats: EMimeType[];
  /** Maximum file size. Defaults to '50MB'. */
  maxFileSize?: string;
  /** Maximum number of files allowed */
  maxFiles?: number;
  /** Called with the server ID (object key) after each successful upload */
  onUploaded?: (key: string, filename: string) => void;
  /**
   * Factory that builds a FilePond process handler for the given enabled variants.
   * Preferred over `processHandler` when image variant selection is needed.
   * Pass `null` to hide the variant checkboxes entirely — for a direct
   * `processHandler` that stores the file as-is and generates no size variants.
   */
  processHandlerFactory?: ((enabledVariants: EImageVariant[]) => ProcessServerConfigFunction) | null;
  /** FilePond server process handler — used when processHandlerFactory is not provided */
  processHandler?: ProcessServerConfigFunction;
  /** Label shown in the drop area */
  label?: string;
  /** Override the minimum height of the drop zone (e.g. '240px'). Defaults to FilePond's built-in height. */
  dropzoneHeight?: string;
  /**
   * Initial set of enabled image variants. Defaults to all variants except original.
   * Ignored when `variantsEditable` is true — all checkboxes start unchecked.
   * Only relevant when `processHandlerFactory` is provided and `allowedFormats` includes images.
   */
  imageVariants?: EImageVariant[];
  /**
   * When true the variant checkboxes are interactive (admin mode).
   * When false (default) they are rendered as read-only indicators.
   */
  variantsEditable?: boolean;
  /**
   * Aspect ratio for the crop tool (width / height). e.g. 1 for square, 16/9 for widescreen.
   * Defaults to free-form (NaN).
   */
  cropAspectRatio?: number;
  /** Number of file thumbnails to show per row. Defaults to 1. */
  uploaderColumns?: number;
  /** Blocks picking files, on top of the built-in "no size selected" block. */
  disabled?: boolean;
  /**
   * Called with `true` while any file is uploading and `false` once none is. Lets a parent dialog
   * refuse to close mid-upload.
   */
  onBusyChange?: (busy: boolean) => void;
};

export function FileUploader({
  allowedFormats,
  maxFileSize = '50MB',
  maxFiles = 1,
  onUploaded,
  processHandlerFactory,
  processHandler: processHandlerProp,
  label,
  dropzoneHeight,
  imageVariants = ALL_VARIANTS_NO_ORIGINAL,
  variantsEditable = false,
  cropAspectRatio = NaN,
  uploaderColumns = 1,
  disabled = false,
  onBusyChange,
}: TFileUploaderProps) {
  const acceptsImages = allowedFormats.some(f => f.startsWith('image/'));
  const showVariants = acceptsImages && processHandlerFactory !== null;
  const theme = useTheme();
  const uid = useId().replace(/:/g, '');
  const scopeClass = `fp-${uid}`;

  const [enabledVariants, setEnabledVariants] = useState<EImageVariant[]>(variantsEditable ? [] : imageVariants);
  // The factory-built handler rejects image uploads with no size, so block picking a file until one is
  // chosen. A direct `processHandler` ignores the checkboxes, so it is never blocked.
  const needsSizeSelection = showVariants && !!processHandlerFactory && enabledVariants.length === 0;

  const pondRef = useRef<FilePondInstance | null>(null);

  const {
    cropOpen,
    renameOpen,
    cropSrc,
    fileName,
    updateFileName,
    handleCropConfirm,
    handleCropSkip,
    handleCropCancel,
    handleRenameConfirm,
    handleDialogExited,
    handleProcessFile,
    onAddFile,
    onRemoveFile,
  } = useCropQueue({ cropAspectRatio, pondRef, onUploaded });

  // Derived from FilePond's own file statuses, so an upload that errors or is aborted can never
  // leave the parent stuck in "busy".
  const syncBusy = useCallback(() => {
    const files = pondRef.current?.getFiles() ?? [];
    onBusyChange?.(files.some(f => f.status === FileStatus.PROCESSING || f.status === FileStatus.PROCESSING_QUEUED));
  }, [onBusyChange]);

  // Keep a ref to enabledVariants so the process handler can read the latest value
  const enabledVariantsRef = useRef<EImageVariant[]>(enabledVariants);
  useEffect(() => {
    enabledVariantsRef.current = enabledVariants;
  }, [enabledVariants]);

  const processHandler = useMemo(
    () => (processHandlerFactory ? processHandlerFactory(enabledVariants) : processHandlerProp!),
    // Re-build handler whenever enabled variants change (only when factory is provided)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [processHandlerFactory, processHandlerProp, enabledVariants],
  );

  // Editable mode picks exactly one size (radio); read-only mode just displays the fixed set.
  const handleVariantSelect = (variant: EImageVariant) => setEnabledVariants([variant]);

  const primaryMain = theme.palette.primary.main;

  const defaultLabel = (() => {
    const hasImages = allowedFormats.some(f => f.startsWith('image/'));
    const hasPdf = allowedFormats.includes(EMimeType.PDF);
    if (hasImages && hasPdf) {
      return 'images or PDFs';
    }
    if (hasImages) {
      return 'images';
    }
    if (hasPdf) {
      return 'PDFs';
    }
    return 'files';
  })();

  return (
    <>
      {showVariants && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            {variantsEditable ? 'Generate size' : 'Generate sizes'}
          </Typography>
          {variantsEditable ? (
            <RadioGroup
              row
              value={enabledVariants[0] ?? ''}
              onChange={(_e, value) => handleVariantSelect(value as EImageVariant)}
            >
              {VARIANT_OPTIONS.map(({ variant, label: variantLabel }) => (
                <FormControlLabel
                  key={variant}
                  value={variant}
                  control={<Radio size="small" />}
                  label={<Typography variant="caption">{variantLabel}</Typography>}
                  sx={{ mr: 1 }}
                />
              ))}
            </RadioGroup>
          ) : (
            <FormGroup row>
              {VARIANT_OPTIONS.map(({ variant, label: variantLabel }) => (
                <FormControlLabel
                  key={variant}
                  control={<Checkbox size="small" checked={enabledVariants.includes(variant)} disabled />}
                  label={<Typography variant="caption">{variantLabel}</Typography>}
                  sx={{ mr: 1 }}
                />
              ))}
            </FormGroup>
          )}
        </Box>
      )}

      {needsSizeSelection && (
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
          Select a size above before choosing a file.
        </Typography>
      )}

      <style>{`
        .${scopeClass} .filepond--root {
          border: 2px dashed ${primaryMain};
          border-radius: 12px;
          overflow: hidden;
          transition: border-color 0.2s, background-color 0.2s;
          ${dropzoneHeight ? `min-height: ${dropzoneHeight};` : ''}
        }
        .${scopeClass} .filepond--root:hover {
          border-color: ${theme.palette.primary.dark};
        }
        .${scopeClass} .filepond--panel-root {
          background-color: ${primaryMain}12;
          border: none;
          box-shadow: none;
        }
        ${
          dropzoneHeight
            ? `
        .${scopeClass} .filepond--drop-label {
          min-height: ${dropzoneHeight};
        }
        `
            : ''
        }
        .${scopeClass} .filepond--drop-label label {
          color: ${theme.palette.primary.dark};
          font-size: 1.3rem;
          font-weight: 500;
        }
        .${scopeClass} .filepond--label-action {
          color: ${primaryMain};
          text-decoration-color: ${primaryMain};
          font-weight: 700;
          font-size: 1.3rem;
        }
        .${scopeClass} .filepond--list {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          padding: 8px;
          position: static !important;
          left: unset !important;
          right: unset !important;
          width: 100% !important;
        }
        .${scopeClass} .filepond--item {
          position: static !important;
          transform: none !important;
          width: calc(${100 / uploaderColumns}% - ${((uploaderColumns - 1) * 8) / uploaderColumns}px) !important;
          margin: 0 !important;
        }
        .${scopeClass} .filepond--image-preview-wrapper {
          height: 120px !important;
        }
        .${scopeClass} .filepond--image-preview {
          height: 120px !important;
        }
        .${scopeClass} .filepond--root[data-disabled],
        .${scopeClass} .filepond--root[data-disabled]:hover {
          border-color: ${theme.palette.grey[400]};
          cursor: not-allowed;
        }
        .${scopeClass} .filepond--root[data-disabled] .filepond--panel-root {
          background-color: ${theme.palette.grey[200]};
        }
        .${scopeClass} .filepond--root[data-disabled] .filepond--drop-label label,
        .${scopeClass} .filepond--root[data-disabled] .filepond--label-action {
          color: ${theme.palette.grey[500]};
          text-decoration-color: ${theme.palette.grey[500]};
        }
      `}</style>
      <div className={scopeClass}>
        <FilePond
          ref={pondRef}
          disabled={needsSizeSelection || disabled}
          allowMultiple={maxFiles > 1}
          maxFiles={maxFiles}
          acceptedFileTypes={allowedFormats as string[]}
          labelFileTypeNotAllowed={`Only ${defaultLabel} are allowed`}
          maxFileSize={maxFileSize}
          labelMaxFileSizeExceeded={`File is too large (max ${maxFileSize})`}
          styleButtonRemoveItemPosition="left"
          imagePreviewHeight={120}
          labelIdle={label ?? `Drag & drop your ${defaultLabel} or <span class="filepond--label-action">Browse</span>`}
          // Every file passes through the crop / rename dialog first; it triggers the upload.
          instantUpload={false}
          onaddfile={(_err, file) => {
            onAddFile(file.file as File, file.id);
          }}
          server={{ process: processHandler }}
          onprocessfilestart={syncBusy}
          onprocessfile={(error, file) => {
            handleProcessFile(error, file);
            syncBusy();
          }}
          onprocessfiles={syncBusy}
          onprocessfileabort={syncBusy}
          onprocessfilerevert={syncBusy}
          onerror={syncBusy}
          onremovefile={(_err, file) => {
            onRemoveFile(file.serverId as string | null, file.id);
            syncBusy();
          }}
          credits={false}
        />
      </div>

      <RenameFileDialog
        open={renameOpen}
        fileName={fileName}
        onFileNameChange={updateFileName}
        onConfirm={handleRenameConfirm}
        onCancel={handleCropCancel}
      />

      {acceptsImages && (
        <CropDialog
          open={cropOpen}
          src={cropSrc}
          cropAspectRatio={cropAspectRatio}
          fileName={fileName}
          onFileNameChange={updateFileName}
          onConfirm={handleCropConfirm}
          onSkip={handleCropSkip}
          onCancel={handleCropCancel}
          onExited={handleDialogExited}
        />
      )}
    </>
  );
}
